import React from 'react';
import { useApolloClient } from '@apollo/client';
import { makeUpdateFunction } from './updateFunctions';
import { adoptUnownedTrackedQueries, countTrackedQueriesNotOwnedBy, getTrackedQueriesForOwner, isTrackedQueryQueued, moveTrackedQueryToFailed, removeTrackedQuery, subscribeReplayRequests, } from './trackedQueries';
import { classifyReplayError } from './replayErrors';
import { isInFlight } from './inFlightTrackedQueries';
export const OfflineMutationLoader = ({ owner, errorHandler, }) => {
    const client = useApolloClient();
    React.useEffect(() => {
        if (!client)
            return;
        // Nobody is signed in — there is no session to attribute writes to.
        // Held mutations (if any) stay queued untouched; they are picked up once
        // their owner signs in again.
        if (owner === null)
            return;
        // Guards against replaying under the WRONG identity when `owner` changes
        // (or the component unmounts) mid-drain — e.g. an identity switch while
        // replay is still in flight. The auth link resolves the JWT per request,
        // so an iteration that starts after the switch would sign the PREVIOUS
        // owner's mutation with the NEW user's token — exactly the bug this plan
        // fixes, so no new iteration may start once `cancelled` is set. A
        // mutation already in flight when cancellation lands cannot be recalled;
        // it is allowed to finish (and, on success, still removed from the
        // queue below — it did reach the server).
        let cancelled = false;
        // Guards against two drains running concurrently (e.g. a "Reintentar"
        // requestReplay() firing while the periodic/mount drain is still on the
        // wire) — see requestDrain() below.
        let draining = false;
        let rerunPending = false;
        const drain = async () => {
            draining = true;
            try {
                // One-time, marker-guarded adoption of pre-BIL-4928 entries that have
                // no owner yet — see adoptUnownedTrackedQueries() for the rationale.
                // A no-op after the first successful run. Kept per-drain: cheap once
                // adopted, and simpler than threading a "did this effect already
                // adopt" flag through.
                await adoptUnownedTrackedQueries(owner);
                if (cancelled)
                    return;
                const trackedQueries = await getTrackedQueriesForOwner(owner);
                if (cancelled)
                    return;
                for (const tracked of trackedQueries) {
                    if (cancelled)
                        break; // do not start a new iteration under a stale owner
                    const { variables, query, context, operationName } = tracked;
                    // The queue was read once, before the loop, so an entry may have
                    // changed since. One already on the wire in this tab (sent by the
                    // queue link as soon as the connection came back) removes or
                    // dead-letters itself, and one that has left the queue was already
                    // handled: sending either again would duplicate it. The in-flight
                    // mark is cleared only after the queue write, so "not in flight and
                    // still queued" is exactly what still has to be sent.
                    if (isInFlight(context.id))
                        continue;
                    if (!(await isTrackedQueryQueued(context.id)))
                        continue;
                    if (cancelled)
                        break; // the read above yielded; same stale-owner guard
                    const update = makeUpdateFunction(operationName, context);
                    try {
                        await client.mutate(Object.assign({ context,
                            variables, mutation: query, update, optimisticResponse: context.optimisticResponse }, context.props));
                        await removeTrackedQuery(context.id);
                    }
                    catch (error) {
                        console.error(error);
                        const classification = classifyReplayError(error);
                        if (classification.kind === 'permanent') {
                            // Business decision by the server — will not change on retry.
                            // Dead-letter it and keep draining the rest of the queue.
                            await moveTrackedQueryToFailed(tracked, classification.message);
                            errorHandler === null || errorHandler === void 0 ? void 0 : errorHandler(error, [tracked]);
                            continue;
                        }
                        errorHandler === null || errorHandler === void 0 ? void 0 : errorHandler(error, [tracked]);
                        break; // transient: stop replay; this and subsequent mutations stay queued for next attempt
                    }
                }
                if (cancelled)
                    return;
                // Held work must not be invisible — see the open product question in
                // plan 004 about what (if anything) eventually happens to it.
                const heldCount = await countTrackedQueriesNotOwnedBy(owner);
                if (heldCount > 0 && !cancelled) {
                    console.warn(`OfflineMutationLoader: ${heldCount} queued mutation(s) belong to a different (or no) identity and were not replayed.`);
                }
            }
            finally {
                draining = false;
            }
            // A replay was requested while this drain was running — run exactly
            // one more, now that the queue/store state has settled. Further
            // requests during THIS rerun just set the flag again, which is fine:
            // each one collapses to at most one extra drain, never a concurrent one.
            if (!cancelled && rerunPending) {
                rerunPending = false;
                await drain();
            }
        };
        // Starts a drain, or — if one is already in flight — marks that another
        // is needed once it finishes. Never starts a new drain once cancelled
        // (owner changed / component unmounted).
        const requestDrain = () => {
            if (cancelled)
                return;
            if (draining) {
                rerunPending = true;
                return;
            }
            drain();
        };
        requestDrain();
        const unsubscribeReplayRequests = subscribeReplayRequests(requestDrain);
        return () => {
            cancelled = true;
            unsubscribeReplayRequests();
        };
    }, [client, owner]);
    return null;
};
